import React from "react";
import { AlertInfo } from "../../../common/Alert";
import BootstrapTable from "react-bootstrap-table-next";
import { FaTrash } from "react-icons/fa";
import { getImage } from "../../../../utils/Utils";
import Empty from "../../../../assets/images/appImages/category.png";
import { useAppDispatch, useAppSelector } from "../../../../redux/hook";
import { Color } from "../../../../utils/theme";
import Swal from "sweetalert2";
import {
  CartItemType,
  decrement,
  increment,
  removeCartItem,
} from "../../../../redux/slice/useCartSlice";
import { useAddProductCartMutation } from "../../../../utils/api/product/product.api";
import { Product } from "../../../../utils/api/product/product.type";
import { formatCurrency } from "../../../../utils/Utils";
import { getVendeur } from "../../../HomePage/HomePage";

/* ─────────────────────────────────────────────
   Helper – résoudre l'image du produit
───────────────────────────────────────────── */
function resolveImage(cell: Product, row: CartItemType): string {
  if (cell?.type === "taille_unique") {
    return cell?.images?.[0]?.image ?? "";
  }
  if (row?.variations?.length) {
    return (
      row.variations[0]?.images?.[0]?.image ??
      cell?.images?.[0]?.image ??
      ""
    );
  }
  return "";
}

/* ─────────────────────────────────────────────
   Carte produit mobile
───────────────────────────────────────────── */
interface CardItemProps {
  item: any;
  devise: string;
  onDelete: (item: any) => void;
  onDecrement: (item: any) => void;
  onIncrement: (item: any) => void;
}

function PanierCardItem({ item, devise, onDelete, onDecrement, onIncrement }: CardItemProps) {
  const imgSrc = resolveImage(item?.produit, item);

  return (
    <div className="panier-card">
      {/* ── Image  ── */}
      <div className="panier-card__img-wrapper">
        <img
          loading="lazy"
          src={imgSrc ? getImage(imgSrc) : Empty}
          alt={item?.produit?.nom ?? "produit"}
          className="panier-card__img"
        />
      </div>

      {/* ── Infos droite ── */}
      <div className="panier-card__body">
        {/* Nom */}
        <p className="panier-card__name">{item?.produit?.nom}</p>

        {/* Marque */}
        {(item?.produit as any)?.marque && (
          <p className="panier-card__brand">{(item.produit as any).marque}</p>
        )}

        {/* Catégorie / taille */}
        {item?.produit?.categorie?.nom && (
          <p className="panier-card__meta">{item.produit.categorie.nom}</p>
        )}
        {item?.variations?.[0]?.taille && (
          <p className="panier-card__meta">Taille : {item.variations[0].taille}</p>
        )}

        {/* Prix unitaire */}
        <p className="panier-card__price">
          {formatCurrency(item?.prix, devise)}
        </p>

        {/* Stepper quantité */}
        <div className="panier-card__qty-row">
          <button
            type="button"
            className="panier-card__qty-btn"
            onClick={() => onDecrement(item)}
            disabled={item?.quantite <= 1}
          >
            −
          </button>
          <span className="panier-card__qty-value">{item?.quantite}</span>
          <button
            type="button"
            className="panier-card__qty-btn"
            onClick={() => onIncrement(item)}
            disabled={item?.quantite >= item?.stock}
          >
            +
          </button>
        </div>

        {/* Total */}
        <p className="panier-card__total">
          {formatCurrency(item?.prix * item?.quantite, devise)}
        </p>

        {/* Supprimer */}
        <button
          type="button"
          className="panier-card__delete"
          title="Supprimer"
          onClick={() => onDelete(item)}
        >
          <FaTrash />
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Composant principal
───────────────────────────────────────────── */
function PanierItemTable() {
  const Vendeur = getVendeur();
  const { user } = useAppSelector((s) => s?.user);
  const dispatch = useAppDispatch();
  const { cartItems } = useAppSelector((state) => state.cart);
  const [addToCart] = useAddProductCartMutation();

  /* ── Suppression avec confirmation ── */
  const supprimerDuPanier = async (produitASupprimer: any) => {
    await Swal.fire({
      title: "Êtes-vous sûr(e) de vouloir supprimer ce produit de votre panier ?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Oui",
      cancelButtonText: "Non",
      showLoaderOnConfirm: true,
      iconColor: Color.themeColor,
      confirmButtonColor: Color.success,
      preConfirm: async () => dispatch(removeCartItem(produitASupprimer)),
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result: any) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: "success",
          title: "Produit supprimé avec succès !",
          iconColor: Color.success,
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: false,
        });
      }
    });
  };

  /* ── le tableau desktop ── */
  const actionFormatter = (_cell: any, row: any) => (
    <div className="table-actions-btn-container-commandes d-flex justify-content-center align-items-center gap-3">
      <div className="container-btn-action-icon-modal">
        <button
          type="button"
          className="btn with-tooltip btn-action-icon-delete"
          data-tooltip-content="Supprimer"
          style={{ color: "#E53E3E" }}
          onClick={() => supprimerDuPanier(row)}
        >
          <FaTrash />
        </button>
      </div>
    </div>
  );

  const productFormatter = (cell: Product, row: CartItemType) => {
    const imgProd = resolveImage(cell, row);
    return (
      <div className="d-flex align-items-center gap-2">
        <div className="product-image-container">
          <img
            loading="lazy"
            src={imgProd ? getImage(imgProd) : Empty}
            alt="image"
            className="cart-image"
          />
        </div>
        <div>
          <p className="product-name-panier mb-1">{cell?.nom}</p>
          <p className="product-category-name mb-0">{cell?.categorie?.nom}</p>
          <p className="product-category-name mb-0">{row?.variations?.[0]?.taille}</p>
        </div>
      </div>
    );
  };

  const quantityFormatter = (_cell: any, row: any) => (
    <div className="d-flex align-items-center justify-content-center">
      <div className="row-quantity">
        {row?.quantite > 1 && (
          <button className="action-btn" type="button" onClick={() => dispatch(decrement(row))}>-</button>
        )}
        <span className="col-md-4 qty_wrapper">{row?.quantite}</span>
        {row?.quantite < row?.stock && (
          <button type="button" className="action-btn" onClick={() => dispatch(increment(row))}>+</button>
        )}
      </div>
    </div>
  );

  const totalFormatter = (_cell: any, row: any) => (
    <div className="d-flex align-items-center justify-content-center">
      {formatCurrency(row?.prix * row?.quantite, Vendeur?.devise)}
    </div>
  );

  const prixFormatter = (_cell: any, row: any) => (
    <div className="d-flex align-items-center justify-content-center">
      {formatCurrency(row?.prix, Vendeur?.devise)}
    </div>
  );

  const columns = [
    {
      dataField: "produit",
      text: "Produits",
      formatter: productFormatter,
      headerStyle: () => ({ width: "200px", whiteSpace: "normal" }),
    },
    {
      dataField: "prix",
      text: "Prix",
      formatter: prixFormatter,
      headerStyle: () => ({ width: "160px", whiteSpace: "normal" }),
    },
    {
      dataField: "quantite",
      text: "Quantité",
      formatter: quantityFormatter,
      headerStyle: () => ({ width: "300px", whiteSpace: "normal" }),
    },
    {
      dataField: "total",
      text: "Total",
      formatter: totalFormatter,
      headerStyle: () => ({ width: "170px", whiteSpace: "normal" }),
    },
    {
      dataField: "id",
      text: "",
      formatter: actionFormatter,
    },
  ];

  const cartItemsWithKey =
    cartItems?.map((item: any, index: number) => ({
      ...item,
      _rowKey: item?.id ?? item?.produit?.id ?? index,
    })) || [];

  return (
    <>
      {/* ── Desktop : tableau Bootstrap original ── */}
      <div className="panier-desktop">
        <BootstrapTable
          keyField="_rowKey"
          data={cartItemsWithKey}
          columns={columns}
          condensed
          hover
          wrapperClasses="table-responsive panier-table"
          noDataIndication={() => <AlertInfo message="Votre panier est vide" />}
        />
      </div>

      {/* ── Mobile : cartes produit ── */}
      <div className="panier-mobile">
        {cartItemsWithKey.length === 0 ? (
          <AlertInfo message="Votre panier est vide" />
        ) : (
          <div className="panier-card-list">
            {cartItemsWithKey.map((item) => (
              <PanierCardItem
                key={item._rowKey}
                item={item}
                devise={Vendeur?.devise}
                onDelete={supprimerDuPanier}
                onDecrement={(i) => dispatch(decrement(i))}
                onIncrement={(i) => dispatch(increment(i))}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default PanierItemTable;